import React from "react";
import PropTypes from "prop-types";
import { Dropdown } from "@learningblocks/shared-components";
import { OpenIcon, CloseIcon } from "./Icons";
import { OptionFeedback } from "../../Components/OptionFeedback";
import { Label } from "../../Components/Label";
import { OptionWrapper } from "../../Components/OptionWrapper";

const CLASS_NAME = {
  CORRECT: "correct",
  INCORRECT: "incorrect",
};

/**
 * Options:
 *  - Renders a single Dropdown option for Step-Select Question
 */
export function Options(props) {
  const getOptions = () => {
    return props.options.map((option) => ({
      ...option,
      name: props.stepID,
    }));
  };

  const selectOption = (params) => {
    if (props.disabled === false) {
      props.selectOption({
        name: params.name,
        value: params.value,
      });
    }
  };

  const wrapperClass = () => {
    return `stepSelect__option ${props.feedback ? getFeedbackClassName() : ""}`;
  };

  const getFeedbackClassName = () =>
    props.selectedOptionID === props.stepID
      ? CLASS_NAME.CORRECT
      : CLASS_NAME.INCORRECT;

  return (
    <>
      <OptionWrapper className={wrapperClass()} selectOption={selectOption}>
        <Label label={props.label} />
        <Dropdown
          className="stepSelect__option dropdown-select"
          placeholder={props.placeholder}
          onChange={selectOption}
          value={props.selectedOptionID}
          disabled={props.disabled}
          options={getOptions()}
          iconOpen={OpenIcon}
          iconClose={CloseIcon}
        />
      </OptionWrapper>
      <OptionFeedback
        visible={!!props.feedback}
        className={getFeedbackClassName()}
        text={`<p>${props.isCorrect ? "Correct. " : "Not quite. "} ${props.feedback}</p>`}
      />
    </>
  );
}

Options.propTypes = {
  /**
   * className to be set for the step option
   */
  className: PropTypes.string,
  /**
   * Boolean value to manage if step disabled
   */
  disabled: PropTypes.bool.isRequired,
  /**
   * Feedback message to displayed corresponding to correct/incorrect option selected.
   */
  feedback: PropTypes.string,
  /**
   * Label for current step
   */
  label: PropTypes.string.isRequired,
  /**
   * List of selectable options
   */
  options: PropTypes.arrayOf(
    PropTypes.shape({
      /**
       * The id for the option
       */
      id: PropTypes.number,
      /**
       * Text to be displayed for the option
       */
      text: PropTypes.string,
    })
  ).isRequired,
  /**
   * Placeholder message to show when step is untouched.
   */
  placeholder: PropTypes.string.isRequired,
  /**
   * Id of the Option that is currently selected for the step
   */
  selectedOptionID: PropTypes.oneOfType([
    PropTypes.oneOf([null]),
    PropTypes.number,
  ]),
  /**
   * Callback function to execute when an
   * option is selected from dropdown
   */
  selectOption: PropTypes.func.isRequired,
  /**
   * Boolvalue specifying if feedback should be displayed
   */
  showFeedback: PropTypes.bool.isRequired,
  /**
   * The ID value for correct Option of step
   */
  stepID: PropTypes.number.isRequired,
};

Options.defaultProps = {
  className: "answer-step",
  options: [],
  disabled: false,
  label: "",
  placeholder: "",
  selectedOptionID: null,
  showFeedback: false,
};
